import request from '@/utils/request';

const PAGE_SIZE = 100;
const MAX_PAGE_COUNT = 50;
const TERRITORY_FILTER_CHUNK_SIZE = 40;
const SUPERIOR_TERRITORY_FILTER_CHUNK_SIZE = 12;
const CHUNK_REQUEST_CONCURRENCY = 3;
const SUBMITTED_STATUS = 'Submitted_vod';
const PLANNED_STATUS = 'Planned_vod';
const MANAGER_TERRITORY_TYPES = ['DM', 'AM', 'SD', 'RPM'];
const TARGET_PHYSICIAN_SEGMENTS = ['A', 'B', 'C'];

function normalizeListResponse(res) {
  if (Array.isArray(res)) {
    return {
      list: res,
      count: res.length,
    };
  }
  if (res && Array.isArray(res.data)) {
    return {
      list: res.data,
      count: Number(res.data_count) || res.data.length,
    };
  }
  if (res && Array.isArray(res.value)) {
    return {
      list: res.value,
      count: Number(res['@odata.count']) || res.value.length,
    };
  }
  return {
    list: [],
    count: 0,
  };
}

function listFromResponse(res) {
  return normalizeListResponse(res).list;
}

async function fetchPagedGateway(url, params = {}) {
  const result = [];
  let dataCount = 0;

  for (let pageIndex = 1; pageIndex <= MAX_PAGE_COUNT; pageIndex += 1) {
    const res = await request({
      url,
      method: 'get',
      params: {
        ...params,
        page_index: pageIndex,
        page_size: PAGE_SIZE,
      },
    });
    const {list, count} = normalizeListResponse(res);
    if (count) dataCount = count;
    result.push(...list);

    if (list.length < PAGE_SIZE || (dataCount && result.length >= dataCount)) {
      break;
    }
  }

  return result;
}

function uniqBy(list, keyGetter) {
  const map = new Map();
  (list || []).forEach((item) => {
    const key = keyGetter(item);
    if (key && !map.has(key)) {
      map.set(key, item);
    }
  });
  return Array.from(map.values());
}

function chunk(list, size) {
  const result = [];
  for (let index = 0; index < list.length; index += size) {
    result.push(list.slice(index, index + size));
  }
  return result;
}

async function mapWithConcurrency(list, concurrency, iterator) {
  const result = new Array(list.length);
  let nextIndex = 0;
  const workerCount = Math.min(concurrency, list.length);
  const workers = Array.from({length: workerCount}, async () => {
    while (nextIndex < list.length) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      result[currentIndex] = await iterator(list[currentIndex], currentIndex);
    }
  });
  await Promise.all(workers);
  return result;
}

function flattenChunkResults(results) {
  return (results || []).reduce((list, item) => list.concat(item || []), []);
}

function buildOrEqualsFilter(field, values = []) {
  const list = (values || []).filter(Boolean).map(item => String(item));
  if (!list.length) return '';
  return `(${list.map(item => `${field} eq '${item.replace(/'/g, "''")}'`).join(' or ')})`;
}

function getTerritoryId(item) {
  return item && item.territory_id ? String(item.territory_id) : '';
}

function isLeafSalesTerritory(item) {
  const type = String(item?.territory_type || '').toUpperCase();
  return Boolean(item?.user_id) && !MANAGER_TERRITORY_TYPES.includes(type);
}

async function fetchTerritoriesBySuperiorIds(superiorTerritoryIds = []) {
  const result = [];
  const idChunks = chunk(
    uniqBy(superiorTerritoryIds.map(id => ({id})), item => item.id).map(item => item.id),
    SUPERIOR_TERRITORY_FILTER_CHUNK_SIZE,
  );

  for (const idChunk of idChunks) {
    const filter = buildOrEqualsFilter('superior_territory_id', idChunk);
    if (!filter) continue;
    const res = await request({
      url: `/api/standard/OData/Territories?$filter=${filter}&$select=territory_id,territory_name,territory_type,superior_territory_id,user_id,user_name`,
      method: 'get',
    });
    result.push(...listFromResponse(res));
  }

  return result;
}

export async function fetchManagerSubordinateTerritories({territoryId, territoryType} = {}) {
  if (!territoryId) return [];

  const {descendants, leafSales} = await fetchManagerSubordinateTerritoryHierarchy({territoryId, territoryType});
  return leafSales.length ? leafSales : descendants.filter(item => item?.user_id);
}

export async function fetchManagerSubordinateTerritoryHierarchy({territoryId, territoryType} = {}) {
  if (!territoryId) {
    return {
      descendants: [],
      leafSales: [],
    };
  }

  const descendants = [];
  let frontier = [territoryId];
  const maxDepth = String(territoryType || '').toUpperCase() === 'DM' ? 1 : 4;

  for (let depth = 0; depth < maxDepth && frontier.length; depth += 1) {
    const children = await fetchTerritoriesBySuperiorIds(frontier);
    const freshChildren = uniqBy(children, getTerritoryId).filter((item) => {
      const id = getTerritoryId(item);
      return id && !descendants.some(existing => getTerritoryId(existing) === id);
    });
    descendants.push(...freshChildren);
    frontier = freshChildren
      .filter(item => !isLeafSalesTerritory(item))
      .map(getTerritoryId)
      .filter(Boolean);
  }

  const leafSales = descendants.filter(isLeafSalesTerritory);
  return {
    descendants,
    leafSales,
  };
}

function buildDateRangeFilter(field, startDate, endDate) {
  const filters = [];
  if (startDate) filters.push(`${field} ge ${startDate}`);
  if (endDate) filters.push(`${field} lt ${endDate}`);
  return filters;
}

function buildScopeFilter({userIds, territoryIds, userField = 'user_id', territoryField = 'territory_id'} = {}) {
  const userFilter = buildOrEqualsFilter(userField, userIds);
  const territoryFilter = buildOrEqualsFilter(territoryField, territoryIds);
  return userFilter || territoryFilter;
}

function buildTargetCustomerFilter(scopeFilter) {
  const segmentFilter = buildOrEqualsFilter('physician_segment', TARGET_PHYSICIAN_SEGMENTS);
  return [scopeFilter, segmentFilter].filter(Boolean).join(' and ');
}

function buildCallFilter({startDate, endDate, status, hasFeedback, territoryIds, userIds} = {}) {
  const filters = buildDateRangeFilter('call_time', startDate, endDate);
  const userFilter = buildOrEqualsFilter('call_user_id', userIds);
  const territoryFilter = buildOrEqualsFilter('call_territory_id', territoryIds);
  if (userFilter) {
    filters.push(userFilter);
  } else if (territoryFilter) {
    filters.push(territoryFilter);
  }
  if (status) filters.push(`crm_status eq '${status}'`);
  if (hasFeedback) {
    filters.push('physician_mr_call__physician_mr_call_key_feedbacks/any()');
  }
  return filters.join(' and ');
}

function buildOwnerDateFilter({
  startDate,
  endDate,
  dateField,
  territoryIds,
  userIds,
  userField = 'user_id',
  territoryField = 'territory_id',
} = {}) {
  const filters = buildDateRangeFilter(dateField, startDate, endDate);
  const userFilter = buildOrEqualsFilter(userField, userIds);
  const territoryFilter = buildOrEqualsFilter(territoryField, territoryIds);
  if (userFilter) {
    filters.push(userFilter);
  } else if (territoryFilter) {
    filters.push(territoryFilter);
  }
  return filters.join(' and ');
}

function buildApplyQuery(filter, groupByFields, countAlias) {
  return `filter(${filter})/groupby((${groupByFields.join(',')}),aggregate($count as ${countAlias}))`;
}

function buildGroupByQuery(filter, groupByFields) {
  return `filter(${filter})/groupby((${groupByFields.join(',')}))`;
}

function fetchODataApply(entityName, applyQuery) {
  return request({
    url: `/api/standard/OData/${entityName}?$apply=${applyQuery}`,
    method: 'get',
  }).then(listFromResponse);
}

function fetchStandardODataApply(entityName, applyQuery) {
  return request({
    url: `/api/standard/OData/${entityName}?$apply=${applyQuery}`,
    method: 'get',
  }).then(listFromResponse);
}

async function fetchODataApplyByOwnerChunks(entityName, params, groupByFields, countAlias) {
  const userIds = params.userIds || [];
  const territoryIds = params.territoryIds || [];
  const useUserFilter = Boolean(userIds.length);
  const scopeIds = useUserFilter ? userIds : territoryIds;

  if (
    Array.isArray(params.userIds) &&
    Array.isArray(params.territoryIds) &&
    !userIds.length &&
    !territoryIds.length
  ) {
    return [];
  }
  const scopeChunks = scopeIds.length
    ? chunk(scopeIds, TERRITORY_FILTER_CHUNK_SIZE)
    : [[]];

  const chunkResults = await mapWithConcurrency(scopeChunks, CHUNK_REQUEST_CONCURRENCY, async (scopeIdChunk) => {
    const filter = buildCallFilter({
      ...params,
      userIds: useUserFilter ? scopeIdChunk : [],
      territoryIds: useUserFilter ? [] : scopeIdChunk,
    });
    return fetchODataApply(
      entityName,
      buildApplyQuery(filter, groupByFields, countAlias),
    );
  });

  return flattenChunkResults(chunkResults);
}

async function fetchODataApplyByOwnerDateChunks(
  entityName,
  params,
  groupByFields,
  countAlias,
  {dateField, userField = 'user_id', territoryField = 'territory_id'} = {},
) {
  const userIds = params.userIds || [];
  const territoryIds = params.territoryIds || [];
  const useUserFilter = Boolean(userIds.length);
  const scopeIds = useUserFilter ? userIds : territoryIds;

  if (
    Array.isArray(params.userIds) &&
    Array.isArray(params.territoryIds) &&
    !userIds.length &&
    !territoryIds.length
  ) {
    return [];
  }

  const scopeChunks = scopeIds.length
    ? chunk(scopeIds, TERRITORY_FILTER_CHUNK_SIZE)
    : [[]];

  const chunkResults = await mapWithConcurrency(scopeChunks, CHUNK_REQUEST_CONCURRENCY, async (scopeIdChunk) => {
    const filter = buildOwnerDateFilter({
      startDate: params.startDate,
      endDate: params.endDate,
      dateField,
      userIds: useUserFilter ? scopeIdChunk : [],
      territoryIds: useUserFilter ? [] : scopeIdChunk,
      userField,
      territoryField,
    });
    return fetchStandardODataApply(
      entityName,
      buildApplyQuery(filter, groupByFields, countAlias),
    );
  });

  return flattenChunkResults(chunkResults);
}

export function fetchManagerCallStats({startDate, endDate, territoryIds, userIds} = {}) {
  return fetchODataApplyByOwnerChunks(
    'PhysicianMrCalls',
    {startDate, endDate, territoryIds, userIds, status: SUBMITTED_STATUS},
    ['call_user_id'],
    'call_count',
  );
}

export function fetchManagerPlannedCallStats({startDate, endDate, territoryIds, userIds} = {}) {
  return fetchODataApplyByOwnerChunks(
    'PhysicianMrCalls',
    {startDate, endDate, territoryIds, userIds, status: PLANNED_STATUS},
    ['call_user_id'],
    'planned_call_count',
  );
}

export function fetchManagerFeedbackCallStats({startDate, endDate, territoryIds, userIds} = {}) {
  return fetchODataApplyByOwnerChunks(
    'PhysicianMrCalls',
    {startDate, endDate, territoryIds, userIds, status: SUBMITTED_STATUS, hasFeedback: true},
    ['call_user_id'],
    'feedback_call_count',
  );
}

export function fetchManagerCoveredCustomerStats({startDate, endDate, territoryIds, userIds} = {}) {
  return fetchODataApplyByOwnerChunks(
    'PhysicianMrCalls',
    {startDate, endDate, territoryIds, userIds, status: SUBMITTED_STATUS},
    ['call_user_id', 'physician_id'],
    'call_count',
  );
}

export async function fetchManagerRecentCalls({
  startDate,
  endDate,
  territoryIds,
  userIds,
  top = 60,
} = {}) {
  if (
    Array.isArray(userIds) &&
    Array.isArray(territoryIds) &&
    !userIds.length &&
    !territoryIds.length
  ) {
    return [];
  }
  const useUserFilter = Boolean(userIds && userIds.length);
  const scopeIds = useUserFilter ? userIds : (territoryIds || []);
  const scopeChunks = scopeIds.length
    ? chunk(scopeIds, TERRITORY_FILTER_CHUNK_SIZE)
    : [[]];
  const result = [];

  for (const scopeIdChunk of scopeChunks) {
    const filterText = [
      buildCallFilter({
        startDate,
        endDate,
        status: SUBMITTED_STATUS,
        userIds: useUserFilter ? scopeIdChunk : [],
        territoryIds: useUserFilter ? [] : scopeIdChunk,
      }),
    ].filter(Boolean).join(' and ');
    const filter = encodeURIComponent(filterText);
    const res = await request({
      url: `/api/standard/OData/PhysicianMrCalls?$filter=${filter}&$select=physician_mr_call_id,physician_id,call_user_id,call_territory_id,call_time,crm_status&$orderby=call_time desc&$top=${top}`,
      method: 'get',
    });
    result.push(...listFromResponse(res));
  }

  return result
    .sort((a, b) => new Date(b.call_time || 0).getTime() - new Date(a.call_time || 0).getTime())
    .slice(0, top);
}

export async function fetchManagerPhysiciansByIds(physicianIds = []) {
  const ids = uniqBy(physicianIds.map(id => ({id: String(id || '')})), item => item.id)
    .map(item => item.id)
    .filter(Boolean);
  if (!ids.length) return [];

  const result = [];
  const idChunks = chunk(ids, TERRITORY_FILTER_CHUNK_SIZE);

  for (const idChunk of idChunks) {
    const filter = encodeURIComponent(buildOrEqualsFilter('physician_id', idChunk));
    const res = await request({
      url: `/api/standard/OData/Physicians?$filter=${filter}&$select=physician_id,physician_name,institution_name`,
      method: 'get',
    });
    result.push(...listFromResponse(res));
  }

  return result;
}

export function fetchManagerPhysicianTypingDefinitions() {
  return request({
    url: '/api/standard/OData/CnfPhysicianSegments?$top=200',
    method: 'get',
  }).then(listFromResponse);
}

export async function fetchManagerTargetProductStats({territoryIds, userIds} = {}) {
  if (
    Array.isArray(userIds) &&
    Array.isArray(territoryIds) &&
    !userIds.length &&
    !territoryIds.length
  ) {
    return [];
  }

  const useUserFilter = Boolean(userIds && userIds.length);
  const scopeIds = useUserFilter ? userIds : (territoryIds || []);
  const scopeChunks = scopeIds.length
    ? chunk(scopeIds, TERRITORY_FILTER_CHUNK_SIZE)
    : [[]];

  const chunkResults = await mapWithConcurrency(scopeChunks, CHUNK_REQUEST_CONCURRENCY, async (scopeIdChunk) => {
    const filter = buildScopeFilter({
      userIds: useUserFilter ? scopeIdChunk : [],
      territoryIds: useUserFilter ? [] : scopeIdChunk,
    });
    if (!filter) return [];
    return fetchStandardODataApply(
      'UserPhysicianProducts',
      buildGroupByQuery(
        buildTargetCustomerFilter(filter),
        ['user_id', 'product_id', 'product_name', 'physician_id'],
      ),
    );
  });

  return flattenChunkResults(chunkResults);
}

export async function fetchManagerTargetCustomerStats({territoryIds, userIds} = {}) {
  if (
    Array.isArray(userIds) &&
    Array.isArray(territoryIds) &&
    !userIds.length &&
    !territoryIds.length
  ) {
    return [];
  }

  const useUserFilter = Boolean(userIds && userIds.length);
  const scopeIds = useUserFilter ? userIds : (territoryIds || []);
  const scopeChunks = scopeIds.length
    ? chunk(scopeIds, TERRITORY_FILTER_CHUNK_SIZE)
    : [[]];

  const chunkResults = await mapWithConcurrency(scopeChunks, CHUNK_REQUEST_CONCURRENCY, async (scopeIdChunk) => {
    const filter = buildScopeFilter({
      userIds: useUserFilter ? scopeIdChunk : [],
      territoryIds: useUserFilter ? [] : scopeIdChunk,
    });
    if (!filter) return [];
    return fetchStandardODataApply(
      'UserPhysicianProducts',
      buildGroupByQuery(
        buildTargetCustomerFilter(filter),
        ['user_id', 'physician_id'],
      ),
    );
  });

  return flattenChunkResults(chunkResults);
}

export async function fetchManagerProductSegmentStats({territoryIds, userIds} = {}) {
  if (
    Array.isArray(userIds) &&
    Array.isArray(territoryIds) &&
    !userIds.length &&
    !territoryIds.length
  ) {
    return [];
  }

  const useUserFilter = Boolean(userIds && userIds.length);
  const scopeIds = useUserFilter ? userIds : (territoryIds || []);
  const scopeChunks = scopeIds.length
    ? chunk(scopeIds, TERRITORY_FILTER_CHUNK_SIZE)
    : [[]];

  const chunkResults = await mapWithConcurrency(scopeChunks, CHUNK_REQUEST_CONCURRENCY, async (scopeIdChunk) => {
    const filter = buildScopeFilter({
      userIds: useUserFilter ? scopeIdChunk : [],
      territoryIds: useUserFilter ? [] : scopeIdChunk,
    });
    if (!filter) return [];
    return fetchStandardODataApply(
      'UserPhysicianProducts',
      buildApplyQuery(
        buildTargetCustomerFilter(filter),
        ['product_id', 'product_name', 'physician_typing'],
        'target_customer_count',
      ),
    );
  });

  return flattenChunkResults(chunkResults);
}

export async function fetchManagerCoveredTargetProductStats({territoryIds, userIds, physicianIds} = {}) {
  if (
    !physicianIds ||
    !physicianIds.length ||
    (
      Array.isArray(userIds) &&
      Array.isArray(territoryIds) &&
      !userIds.length &&
      !territoryIds.length
    )
  ) {
    return [];
  }

  const useUserFilter = Boolean(userIds && userIds.length);
  const scopeIds = useUserFilter ? userIds : (territoryIds || []);
  const scopeChunks = scopeIds.length
    ? chunk(scopeIds, TERRITORY_FILTER_CHUNK_SIZE)
    : [[]];
  const physicianChunks = chunk(physicianIds, TERRITORY_FILTER_CHUNK_SIZE);
  const requests = [];

  for (const scopeIdChunk of scopeChunks) {
    const scopeFilter = buildScopeFilter({
      userIds: useUserFilter ? scopeIdChunk : [],
      territoryIds: useUserFilter ? [] : scopeIdChunk,
    });
    if (!scopeFilter) continue;

    for (const physicianIdChunk of physicianChunks) {
      const physicianFilter = buildOrEqualsFilter('physician_id', physicianIdChunk);
      if (!physicianFilter) continue;
      requests.push({scopeFilter, physicianFilter});
    }
  }

  const chunkResults = await mapWithConcurrency(requests, CHUNK_REQUEST_CONCURRENCY, item => fetchStandardODataApply(
    'UserPhysicianProducts',
    buildGroupByQuery(
      `${buildTargetCustomerFilter(item.scopeFilter)} and ${item.physicianFilter}`,
      ['user_id', 'product_id', 'physician_id'],
    ),
  ));

  return flattenChunkResults(chunkResults);
}

export async function fetchManagerContentSendStats({startDate, endDate, territoryIds, userIds} = {}) {
  if (
    Array.isArray(userIds) &&
    Array.isArray(territoryIds) &&
    !userIds.length &&
    !territoryIds.length
  ) {
    return [];
  }

  const useUserFilter = Boolean(userIds && userIds.length);
  const scopeIds = useUserFilter ? userIds : (territoryIds || []);
  const scopeChunks = scopeIds.length
    ? chunk(scopeIds, TERRITORY_FILTER_CHUNK_SIZE)
    : [[]];

  const chunkResults = await mapWithConcurrency(scopeChunks, CHUNK_REQUEST_CONCURRENCY, async (scopeIdChunk) => {
    const filter = buildOwnerDateFilter({
      startDate,
      endDate,
      dateField: 'send_time',
      userIds: useUserFilter ? scopeIdChunk : [],
      territoryIds: useUserFilter ? [] : scopeIdChunk,
      userField: 'sender_user_id',
      territoryField: 'sender_territory_id',
    });
    return fetchStandardODataApply(
      'LogContentSends',
      buildApplyQuery(
        filter,
        ['log_content_send_id', 'sender_user_id', 'receiver_physician_id'],
        'send_count',
      ),
    );
  });

  return flattenChunkResults(chunkResults);
}

function buildCampaignTaskDateFilter(startDate, endDate) {
  const filters = [];
  if (endDate) filters.push(`event_start_date lt ${endDate}`);
  if (startDate) filters.push(`event_end_date ge ${startDate}`);
  return filters;
}

function mergeCampaignTaskStats(taskStats = [], executionStats = []) {
  const map = new Map();
  const ensureRow = (userId) => {
    const id = String(userId || '');
    if (!id) return null;
    if (!map.has(id)) {
      map.set(id, {
        user_id: id,
        task_no: 0,
        accept_no: 0,
        ignore_no: 0,
      });
    }
    return map.get(id);
  };

  taskStats.forEach((stat) => {
    const row = ensureRow(stat.user_id);
    if (row) row.task_no += Number(stat.task_count || 0) || 0;
  });

  executionStats.forEach((stat) => {
    const row = ensureRow(stat.user_id);
    if (!row) return;
    const type = String(stat.execution_type || '').toLowerCase();
    const count = Number(stat.execution_count || 0) || 0;
    if (type === 'accept' || type === 'accepted') row.accept_no += count;
    if (type === 'ignore' || type === 'ignored') row.ignore_no += count;
  });

  return Array.from(map.values());
}

export async function fetchManagerCampaignEventTaskStats({startDate, endDate, userIds} = {}) {
  if (Array.isArray(userIds) && !userIds.length) return [];

  const scopeChunks = userIds && userIds.length
    ? chunk(userIds, TERRITORY_FILTER_CHUNK_SIZE)
    : [[]];

  const chunkResults = await mapWithConcurrency(scopeChunks, CHUNK_REQUEST_CONCURRENCY, async (userIdChunk) => {
    const taskFilters = [
      buildOrEqualsFilter('user_id', userIdChunk),
      ...buildCampaignTaskDateFilter(startDate, endDate),
    ].filter(Boolean);
    const requests = [];
    if (taskFilters.length) {
      requests.push(fetchStandardODataApply(
        'CampaignEventTasks',
        buildApplyQuery(
          taskFilters.join(' and '),
          ['user_id'],
          'task_count',
        ),
      ));
    } else {
      requests.push(Promise.resolve([]));
    }

    const executionFilter = buildOwnerDateFilter({
      startDate,
      endDate,
      dateField: 'execution_time',
      userIds: userIdChunk,
      userField: 'user_id',
      territoryField: 'territory_id',
    });
    if (executionFilter) {
      requests.push(fetchStandardODataApply(
        'CampaignEventExecutions',
        buildApplyQuery(
          executionFilter,
          ['user_id', 'execution_type'],
          'execution_count',
        ),
      ));
    } else {
      requests.push(Promise.resolve([]));
    }

    const [taskStats, executionStats] = await Promise.all(requests);
    return {taskStats, executionStats};
  });

  return mergeCampaignTaskStats(
    chunkResults.reduce((list, item) => list.concat(item?.taskStats || []), []),
    chunkResults.reduce((list, item) => list.concat(item?.executionStats || []), []),
  );
}

export function fetchManagerNbaSuggestionStats({startDate, endDate, userIds} = {}) {
  return fetchODataApplyByOwnerDateChunks(
    'NbaSuggestions',
    {startDate, endDate, territoryIds: [], userIds},
    ['user_id'],
    'suggestion_count',
    {
      dateField: 'generated_time',
      userField: 'user_id',
      territoryField: 'user_id',
    },
  );
}

export function fetchManagerNbaSuggestionFeedbackStats({startDate, endDate, territoryIds, userIds} = {}) {
  return fetchODataApplyByOwnerDateChunks(
    'NbaSuggestionFeedbacks',
    {startDate, endDate, territoryIds, userIds},
    ['user_id', 'feedback_status'],
    'feedback_count',
    {
      dateField: 'feedback_time',
      userField: 'user_id',
      territoryField: 'territory_id',
    },
  );
}

export function fetchManagerSubCalls() {
  return fetchPagedGateway('/api/gateway/CRM/GetSubCalls');
}

export async function fetchManagerSubCallFeedbacks(callIds = []) {
  const feedbacks = await fetchPagedGateway('/api/gateway/CRM/GetSubCallFeedbacks');
  const callIdSet = new Set((callIds || []).filter(Boolean).map(item => String(item)));
  if (!callIdSet.size) return feedbacks;

  return feedbacks.filter((item) => {
    const callId = item.physician_mr_call_id || item.source_msl_call_id || item.call_id;
    return callIdSet.has(String(callId || ''));
  });
}

export function fetchManagerCampaignEventTasks({userId, roleId, isExecuted}) {
  return fetchPagedGateway('/api/gateway/Campaign/GetCampaignEventTasks', {
    user_id: userId,
    role_id: roleId,
    is_executed: isExecuted,
  });
}

export function fetchManagerCampaignEventExecutions({startDate, endDate} = {}) {
  const filters = [];
  if (startDate) filters.push(`execution_time ge ${startDate}`);
  if (endDate) filters.push(`execution_time lt ${endDate}`);
  const filterQuery = filters.length ? `?$filter=${filters.join(' and ')}&$top=1000` : '?$top=1000';

  return request({
    url: `/api/standard/odata/CampaignEventExecutions${filterQuery}`,
    method: 'get',
  }).then(listFromResponse);
}
